import { motion } from 'framer-motion'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '../../i18n'
import { localizedPath } from '../../i18n/routes'
import type { FulfillmentTab, FulfillmentTabId } from '../../data/fulfillment'

const AUTO_ADVANCE_MS = 5200

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const TAB_ICONS: Record<FulfillmentTabId, ReactNode> = {
  delivery: (
    <svg {...ICON_PROPS}>
      <rect x="2" y="9" width="11" height="8" rx="1" />
      <path d="M13 11h4l3 3v3h-7" />
      <circle cx="6.5" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </svg>
  ),
  packaging: (
    <svg {...ICON_PROPS}>
      <path d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4 9-4M12 11v10" />
    </svg>
  ),
  returns: (
    <svg {...ICON_PROPS}>
      <path d="M3 7h11a5 5 0 0 1 0 10H8" />
      <path d="M6 4L3 7l3 3" />
    </svg>
  ),
  inventory: (
    <svg {...ICON_PROPS}>
      <path d="M4 20V10M10 20V4M16 20v-8M21 20H3" />
    </svg>
  ),
}

function PanelContent({
  tab,
  tabCta,
  locale,
}: {
  tab: FulfillmentTab
  tabCta: string
  locale: Locale
}) {
  return (
    <div className="relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-ink-100 p-6 sm:p-8 lg:p-10 overflow-hidden h-full">
      <motion.span
        aria-hidden
        animate={{ scale: [1, 1.12, 1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.25), transparent 70%)' }}
      />
      <div className="relative">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold tracking-[-0.04em] leading-none text-[clamp(2.2rem,4.5vw,3.2rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
            {tab.statValue}
          </span>
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-ink-500">
            {tab.statLabel}
          </span>
        </div>
        <h3 className="mt-5 text-xl sm:text-2xl font-semibold text-ink-950 tracking-tight">
          {tab.title}
        </h3>
        <p className="mt-3 text-sm sm:text-base text-ink-600 leading-relaxed">{tab.desc}</p>
        <ul className="mt-5 space-y-2.5">
          {tab.points.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm text-ink-700">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="mt-0.5 text-accent-600 shrink-0">
                <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {p}
            </li>
          ))}
        </ul>
        <Link
          to={localizedPath(tab.serviceKey, locale)}
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 hover:text-accent-800 transition-colors"
        >
          {tabCta}
          <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  )
}

type Props = {
  tabs: FulfillmentTab[]
  tabCta: string
  locale: Locale
}

/**
 * Section « l'avantage D2C » : liste verticale de features à gauche, panneau
 * détaillé animé à droite. Auto-avance jusqu'à la première interaction.
 * Tous les panneaux restent dans le DOM (hidden) pour le prerender/SEO.
 */
export default function D2CFeatureTabs({ tabs, tabCta, locale }: Props) {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (paused) return
    const id = setInterval(() => setActive((a) => (a + 1) % tabs.length), AUTO_ADVANCE_MS)
    return () => clearInterval(id)
  }, [paused, tabs.length])

  const select = (i: number) => {
    setPaused(true)
    setActive(i)
  }

  return (
    <div ref={containerRef} className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-4 lg:gap-6 items-stretch">
      {/* Liste des onglets — flex-1 : la colonne s'étire à la hauteur du panneau */}
      <div role="tablist" aria-orientation="vertical" className="flex flex-col gap-2">
        {tabs.map((tab, i) => {
          const isActive = active === i
          return (
            <button
              key={tab.id}
              id={`d2c-tab-${tab.id}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`d2c-panel-${tab.id}`}
              onClick={() => select(i)}
              className={`relative flex lg:flex-1 items-center gap-3.5 rounded-2xl border px-4 sm:px-5 py-4 text-left transition-all cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500 ${
                isActive
                  ? 'border-accent-200 bg-white shadow-[0_20px_50px_-22px_rgba(59,93,255,0.4)]'
                  : 'border-ink-100 bg-ink-100/60 hover:border-accent-200 hover:bg-white'
              }`}
            >
              {/* Barre de progression latérale de l'onglet actif */}
              <span
                aria-hidden
                className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full overflow-hidden ${
                  isActive ? 'bg-accent-100' : 'bg-transparent'
                }`}
              >
                {isActive && (
                  <motion.span
                    key={`${tab.id}-${paused}`}
                    initial={{ height: paused ? '100%' : '0%' }}
                    animate={{ height: '100%' }}
                    transition={
                      paused
                        ? { duration: 0 }
                        : { duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }
                    }
                    className="block w-full bg-accent-600"
                  />
                )}
              </span>
              <span
                className={`flex items-center justify-center w-10 h-10 rounded-xl border shrink-0 transition-colors ${
                  isActive
                    ? 'bg-gradient-to-br from-accent-600 to-accent-700 border-accent-600 text-white shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]'
                    : 'bg-white border-ink-200 text-ink-500'
                }`}
              >
                {TAB_ICONS[tab.id]}
              </span>
              <span
                className={`text-sm sm:text-base font-semibold tracking-tight transition-colors ${
                  isActive ? 'text-ink-950' : 'text-ink-600'
                }`}
              >
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>

      {/* Panneaux — tous dans le DOM (SEO du prerender), empilés dans la même
          cellule de grid. Le switch est une transition CSS pure : l'état final
          ne dépend d'aucune animation JS (robuste rAF throttlé / prerender). */}
      <div className="relative grid min-h-[380px]">
        {tabs.map((tab, i) => {
          const isActive = active === i
          return (
            <div
              key={tab.id}
              id={`d2c-panel-${tab.id}`}
              role="tabpanel"
              aria-labelledby={`d2c-tab-${tab.id}`}
              aria-hidden={!isActive}
              className={`[grid-area:1/1] transition-[opacity,transform,visibility] duration-500 ease-out ${
                isActive
                  ? 'opacity-100 translate-y-0 visible'
                  : 'opacity-0 translate-y-3 invisible pointer-events-none'
              }`}
            >
              <PanelContent tab={tab} tabCta={tabCta} locale={locale} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
